import React, { useState, useRef, useEffect } from "react";
import SectionHeader from "../../Common/SectionHeader/SectionHeader";
import TopNavBar from "../../TopNavBar/TopNavBar";
import { Modal } from "bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  addPolicy,
  getPolicyDocumentsEnglishMedical,
  getQuestionsMedical,
} from "../../../Redux/IndividualMedicalRedux";
import { addPolicyAffordable } from "../../../Redux/AffordableMedicalRedux";
import { addPolicyUnified } from "../../../Redux/UnifiedDomesticRedux";
import { useTranslation } from "react-i18next";
import UploadDocument from "../../UploadDocument/UploadDocument";
import { documentUpload } from "../../../Redux/loadingReducer";
import "./HealthQuestionsStyle.scss";
import { updateLeadsMedical } from "../../../Redux/LogsRedux/Medical/MedicalLeadsRedux";
import CustomModal from "../../Common/CustomModal/CustomModal";

function HealthQuestions() {
  const navigate = useNavigate();
  const [show, setShow] = useState(true);
  const modalRef = useRef(null);
  const bsModalRef = useRef(null);
  const canvasRef = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [understandCoverage, setUnderstandCoverage] = useState(false);
  const [understandExclusions, setUnderstandExclusions] = useState(false);
  const [questions, setQuestion] = useState([]);
  const [map, setMap] = useState([]);
  const [rawQuestions, setRawQuestions] = useState(null);
  const [showUploadStep, setShowUploadStep] = useState(false);
  const [submittedPlanDetails, setSubmittedPlanDetails] = useState(null);
  const [fileName, setFileName] = useState();
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [signatureTimestamp, setSignatureTimestamp] = useState("");

  const dispatch = useDispatch();

  const location = useLocation();
  const { planDetails } = location.state || {};
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useSelector((state) => state.loading);
  const isAr = selectedLanguage === "ar" || i18n.language?.startsWith("ar");

  // answers/details are arrays indexed by question
  const [answers, setAnswers] = useState([]);
  const [details, setDetails] = useState([]);

  let allAnswered = "";
  if (answers && Array.isArray(answers)) {
    allAnswered = answers.every((ans) => ans === "yes" || ans === "no");
  }

  // Fetch raw question data once on mount
  useEffect(() => {
    if (modalRef.current) {
      bsModalRef.current = new Modal(modalRef.current, {
        backdrop: "static",
        keyboard: false,
      });
      bsModalRef.current.show();
      modalRef.current.addEventListener("hidden.bs.modal", () =>
        setShow(false),
      );
    }

    dispatch(getQuestionsMedical())
      .then((result) => {
        if (result.success && Array.isArray(result.data)) {
          // store raw data so we can switch languages locally without re-fetching
          setRawQuestions(result.data);
        }
      })
      .catch((err) => {
        console.error("Error fetching questions:", err);
      });
  }, []);

  // Map rawQuestions to displayed `questions` when language changes
  useEffect(() => {
    if (!rawQuestions || !Array.isArray(rawQuestions)) return;

    if (selectedLanguage === "en") {
      const questionList = rawQuestions.map((item) => item.English);
      const mapList = rawQuestions.map((item) => item.key);
      setQuestion(questionList);
      setMap(mapList);

      // initialize answers array only if not already set or length mismatch
      if (!Array.isArray(answers) || answers.length !== questionList.length) {
        setAnswers(Array(questionList.length).fill(""));
      }
    } else if (selectedLanguage === "ar") {
      const questionList = rawQuestions.map((item) => item.Arabic);
      const mapList = rawQuestions.map((item) => item.key);
      setQuestion(questionList);
      setMap(mapList);

      if (!Array.isArray(answers) || answers.length !== questionList.length) {
        setAnswers(Array(questionList.length).fill(""));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage, rawQuestions]);

  const handleClose = () => {
    if (bsModalRef.current) bsModalRef.current.hide();
  };

  const handleAnswer = (index, answer) => {
    const newAnswers = [...answers];
    newAnswers[index] = answer;
    setAnswers(newAnswers);
  };

  const handleDetailChange = (index, value) => {
    const newDetails = [...details];
    newDetails[index] = value;
    setDetails(newDetails);
  };

  const formatBrowserTimestamp = () =>
    new Date().toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  const drawSignatureTimestamp = (ctx, canvas, timestamp) => {
    if (!ctx || !canvas) return;

    const footerHeight = 24;
    ctx.clearRect(0, canvas.height - footerHeight, canvas.width, footerHeight);
    if (!timestamp) return;
    ctx.font = "12px Arial";
    ctx.fillStyle = "#555";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.fillText(timestamp, canvas.width - 10, canvas.height - footerHeight / 2);
  };

  // Signature Canvas Logic
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let drawing = false;
    let hasDrawnStroke = false;

    const startDrawing = (e) => {
      drawing = true;
      hasDrawnStroke = false;
      drawSignatureTimestamp(ctx, canvas, "");
      ctx.beginPath();
      ctx.moveTo(
        e.clientX - canvas.getBoundingClientRect().left,
        e.clientY - canvas.getBoundingClientRect().top,
      );
    };

    const draw = (e) => {
      if (!drawing) return;
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.strokeStyle = "#000";
      ctx.lineTo(
        e.clientX - canvas.getBoundingClientRect().left,
        e.clientY - canvas.getBoundingClientRect().top,
      );
      ctx.stroke();
      hasDrawnStroke = true;
      setIsSigned(true);
    };

    const stopDrawing = () => {
      if (!drawing) return;
      drawing = false;
      if (hasDrawnStroke) {
        const timestamp = formatBrowserTimestamp();
        drawSignatureTimestamp(ctx, canvas, timestamp);
        setSignatureTimestamp(timestamp);
      }
    };

    canvas.addEventListener("mousedown", startDrawing);
    canvas.addEventListener("mousemove", draw);
    canvas.addEventListener("mouseup", stopDrawing);
    canvas.addEventListener("mouseleave", stopDrawing);

    // Touch events for mobile
    canvas.addEventListener("touchstart", (e) => {
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent("mousedown", {
        clientX: touch.clientX,
        clientY: touch.clientY,
      });
      canvas.dispatchEvent(mouseEvent);
    });

    canvas.addEventListener("touchmove", (e) => {
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent("mousemove", {
        clientX: touch.clientX,
        clientY: touch.clientY,
      });
      canvas.dispatchEvent(mouseEvent);
    });

    canvas.addEventListener("touchend", () => {
      const mouseEvent = new MouseEvent("mouseup");
      canvas.dispatchEvent(mouseEvent);
    });

    return () => {
      canvas.removeEventListener("mousedown", startDrawing);
      canvas.removeEventListener("mousemove", draw);
      canvas.removeEventListener("mouseup", stopDrawing);
      canvas.removeEventListener("mouseleave", stopDrawing);
    };
  }, []);

  const handleClearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setIsSigned(false);
    setSignatureTimestamp("");
  };

  const canProceed =
    allAnswered &&
    isSigned &&
    agreeTerms &&
    understandCoverage &&
    understandExclusions;

  const handleBack = () => {
    navigate("/personal-information", {
      state: { planDetails },
    });
  };

  const getSignatureDataUrl = () => {
    const canvas = canvasRef.current;
    if (!canvas) return "";
    return canvas.toDataURL();
  };

  const handleConfirm = () => {};

  const handleSubmit = async () => {
    const selectedLimit = planDetails?.selectedLimit || {};
    const selectedPlanName = planDetails?.selectedPlan?.planName || "";
    const planType = selectedPlanName.split(" ")[0] || selectedPlanName;

    const annual_limit =
      selectedLimit.annualLimit != null
        ? Number(selectedLimit.annualLimit).toLocaleString()
        : "";

    const op =
      selectedLimit.opDetectable != null
        ? String(selectedLimit.opDetectable)
        : "";

    const co_pay =
      selectedLimit.coPay != null
        ? (() => {
            const raw = String(selectedLimit.coPay).trim();
            return raw.includes("%") ? raw : `${raw}%`;
          })()
        : selectedLimit.coPay || "";

    // personal information: prefer planDetails.personalInformation, fallback to basicInfo
    const personal =
      planDetails?.personalInformation ||
      planDetails?.basicInfo ||
      planDetails?.personalInformation?.personalInformation ||
      {};

    const answersMapped = {};
    answers.forEach((ans, idx) => {
      const key = map[idx];
      if (key)
        answersMapped[key] = ans === "" ? "No" : ans === "yes" ? "Yes" : "No";
    });

    const rawDataUrl = getSignatureDataUrl();
    const rawBase64 = rawDataUrl ? rawDataUrl.split(",")[1] || "" : "";

    const payload = {
      cover_type: planDetails?.personalInformation?.cover_type || "",
      application_type:
        planDetails?.personalInformation?.application_type || "",
      plan_type: planDetails?.personalInformation?.plan_type || "",
      plan: planDetails?.personalInformation?.plan || "",
      network_type: planDetails?.personalInformation?.network_type || "",
      nationality: personal?.nationality || "",
      civil_id: personal?.civil_id || personal?.civilId || "",
      expiryDateCivilId: personal?.expiryDateCivilId || "",
      annual_limit: annual_limit || "",
      op: op || "",
      co_pay: co_pay || "",
      send_documents_via_whatsapp:
        planDetails?.personalInformation?.send_documents_via_whatsapp || false,
      personalInformation: {
        sponsorName:
          planDetails?.personalInformation?.personalInformation?.sponsorName ||
          "",
        sponsor_id:
          planDetails?.personalInformation?.personalInformation?.sponsor_id ||
          "",
        sponsor_type:
          planDetails?.personalInformation?.personalInformation?.sponsor_type ||
          "",
        fullName:
          planDetails?.personalInformation?.personalInformation?.fullName || "",
        email:
          planDetails?.personalInformation?.personalInformation?.email || "",
        mobileNumber:
          planDetails?.personalInformation?.personalInformation?.mobileNumber ||
          "",
        dob: personal?.dob || planDetails?.basicInfo?.dob || "",
        resident_location:
          planDetails?.personalInformation?.personalInformation
            ?.resident_location || "",
        gender: personal?.gender || planDetails?.basicInfo?.gender || "",
        is_married:
          personal?.is_married ||
          personal?.married ||
          planDetails?.basicInfo?.married ||
          "",
        height:
          planDetails?.personalInformation?.personalInformation?.height || "",
        weight:
          planDetails?.personalInformation?.personalInformation?.weight || "",
        occupation:
          planDetails?.personalInformation?.personalInformation?.occupation ||
          "",
        occupation_code:
          planDetails?.personalInformation?.personalInformation
            ?.occupation_code || "",
        passport:
          planDetails?.personalInformation?.personalInformation?.passport || "",
        id_expiry_date:
          planDetails?.personalInformation?.personalInformation
            ?.id_expiry_date || "",
        phone:
          planDetails?.personalInformation?.personalInformation?.phone || "",
      },
      amount:
        planDetails?.quoteDetails?.amount || planDetails?.quoteDetails || {},
      ...answersMapped,
      agree_terms: agreeTerms,
      understood_coverage: understandCoverage,
      understood_exclusions: understandExclusions,
      signature: rawBase64,
    };

    if (planDetails?.coverPlan == "Individual Medical") {
      dispatch(addPolicy(payload)).then((result) => {
        if (result.success) {
          const payloadData = {
            questions: answersMapped,
            transId: localStorage.getItem("MEDICAL_LEAD_ID"),
            quoteNo: result.data.policy_number,
            signature: "data:image/png;base64," + rawBase64,
          };
          dispatch(updateLeadsMedical(payloadData)).then((response) => {
            if (response.success) {
              localStorage.setItem("MEDICAL_QUOTE_ID", response.data.quoteId);
              const updatedData = {
                ...planDetails,
                policyResult: result.data,
                healthQuestion: answersMapped,
              };
              dispatch(documentUpload(false));
              setSubmittedPlanDetails(updatedData);
              // setShowUploadStep(true);
              setShowConfirm(true);
            }
          });
        }
      });
    } else if (planDetails?.coverPlan == "Affordable Health Cover") {
      dispatch(addPolicyAffordable(payload)).then((result) => {
        if (result.success) {
          const payloadData = {
            questions: answersMapped,
            transId: localStorage.getItem("MEDICAL_LEAD_ID"),
            quoteNo: result.data.policy_number,
            signature: "data:image/png;base64," + rawBase64,
          };
          dispatch(updateLeadsMedical(payloadData)).then((response) => {
            localStorage.setItem("MEDICAL_QUOTE_ID", response.data.quoteId);
            if (response.success) {
              const updatedData = {
                ...planDetails,
                policyResult: result.data,
                healthQuestion: answersMapped,
              };
              dispatch(documentUpload(false));
              setSubmittedPlanDetails(updatedData);
              // setShowUploadStep(true);
              setShowConfirm(true);
            }
          });
        }
      });
    } else if (planDetails?.coverPlan == "Unified Domestic Worker") {
      dispatch(addPolicyUnified(payload)).then((result) => {
        if (result.success) {
          const payloadData = {
            questions: answersMapped,
            transId: localStorage.getItem("MEDICAL_LEAD_ID"),
            quoteNo: result.data.policy_number,
            signature: "data:image/png;base64," + rawBase64,
          };
          dispatch(updateLeadsMedical(payloadData)).then((response) => {
            localStorage.setItem("MEDICAL_QUOTE_ID", response.data.quoteId);
            if (response.success) {
              const updatedData = {
                ...planDetails,
                policyResult: result.data,
                healthQuestion: answersMapped,
              };
              dispatch(documentUpload(false));
              setSubmittedPlanDetails(updatedData);
              // setShowUploadStep(true);
              setShowConfirm(true);
            }
          });
        }
      });
    }
  };

  const getDocumentKey = (documentName) => {
    const planKey =
      planDetails?.selectedPlan?.planKey ||
      planDetails?.selectedPlan?.planName?.toLowerCase().split(" ")[0] ||
      "green";

    if (["network-list", "table-of-benefits"].includes(documentName)) {
      return `${documentName}-${planKey}`;
    }

    return documentName;
  };

  const handlePreview = async (policy, fileName) => {
    setFileName(fileName);

  try {
    const documentKey = getDocumentKey(policy);

    const res = await dispatch(
      getPolicyDocumentsEnglishMedical(documentKey)
    );

    if (!res || !res.data) {
      throw new Error("No document data received");
    }

    const pdfUrl = res.data;

    setSelectedPdf(pdfUrl);

    const proxyUrl = `${process.env.REACT_APP_API_URL}/proxy-pdf?url=${encodeURIComponent(pdfUrl)}`;

    setPreviewUrl(proxyUrl);
    setIsSummaryOpen(true);
  } catch (err) {
    console.error("PDF preview failed:", err);
  }
};

  return (
    <>
      <CustomModal
        isOpen={isSummaryOpen}
        onClose={() => {
          setIsSummaryOpen(false);
          setSelectedPdf(null);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
        }}
        title={fileName}
        download={true}
        pdfUrl={selectedPdf}
      >
        <div
          className="modal-body"
          style={{ overflow: "hidden", padding: 0, height: "70vh" }}
        >
          {previewUrl && (
            <iframe
              title="View Document"
              src={`${previewUrl}#page=1&toolbar=0`}
              width="100%"
              height="95%"
              style={{ border: "none" }}
            />
          )}
        </div>
      </CustomModal>
      <div className="row">
        {/* Left Side */}
        <div className="col-md-12">
          <h3 className="title pt-4 pb-3">
            {t("Please answer the following")}
          </h3>

          {/* <button
                                    type="button"
                                    className="btn-close mb-3"
                                    onClick={handleClose}
                                ></button> */}

          {questions.map((question, index) => (
            <div key={index} className="mb-4 question-item">
              <div className="row align-items-center">
                <div className="col-md-8">
                  <h6 className="mb-0 question-text">{question}</h6>
                </div>
                <div className="col-md-4 d-flex justify-content-md-end pt-2 pt-md-0">
                  <div className="d-flex gap-3">
                    <button
                      type="button"
                      className={`btn btn-secondary shadow-sm px-5 py-2 ${answers[index] === "yes" ? "active" : ""}`}
                      onClick={() => handleAnswer(index, "yes")}
                      aria-pressed={answers[index] === "yes"}
                    >
                      {t("Yes")}
                    </button>

                    <button
                      type="button"
                      className={`btn btn-secondary shadow-sm px-5 py-2 ${answers[index] === "no" ? "active" : ""}`}
                      onClick={() => handleAnswer(index, "no")}
                      aria-pressed={answers[index] === "no"}
                    >
                      {t("No")}
                    </button>
                  </div>
                </div>
              </div>

              {answers[index] === "yes" && (
                <div className="row">
                  <div className="col-md-6">
                    <div className="mt-3">
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder={t("Please provide more details")}
                        value={details[index]}
                        onChange={(e) =>
                          handleDetailChange(index, e.target.value)
                        }
                      ></textarea>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* SIGNATURE SECTION */}
          <div className="mt-5">
            <h6 className="fw-bold mb-3">
              {t("Please put your signature below")} :
            </h6>
            <div style={{ width: "100%", maxWidth: "400px" }}>
              <canvas
                ref={canvasRef}
                width="350"
                height="150"
                className="shadow-sm"
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                }}
              ></canvas>
              <button
                className="btn btn-primary px-4 py-2 mt-2"
                type="button"
                onClick={handleClearSignature}
              >
                {t("Clear")}
              </button>
              {signatureTimestamp && (
                <div className="mt-2 text-muted small">
                  {t("Signed at")}: {signatureTimestamp}
                </div>
              )}
            </div>
          </div>

          {/* TERMS SECTION */}
          {planDetails?.coverPlan === "Individual Medical" ? (
            <div className="mt-4">
              <div className={`form-check mb-3 ${isAr ? "form-check-reverse text-end" : ""}`}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="terms1"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                />
                <label className="form-check-label">
                  {t("I agree to the")}{" "}
                  <a
                    className="link-color"
                    onClick={() =>
                      handlePreview("table-of-benefits", "Table Of Benefits")
                    }
                  >
                    {t("Terms and Conditions")}
                  </a>
                </label>
              </div>

              <div className={`form-check mb-3 ${isAr ? "form-check-reverse text-end" : ""}`}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="terms2"
                  checked={understandCoverage}
                  onChange={(e) => setUnderstandCoverage(e.target.checked)}
                />
                <label className="form-check-label">
                  {t("Have Seen Individual")}{" "}
                  <a
                    onClick={() =>
                      handlePreview("table-of-benefits", "Table Of Benefits")
                    }
                    className="link-color"
                  >
                    {t("Table of Benefits")}
                  </a>{" "}
                  {t("I have also understood the")}{" "}
                  <a
                    onClick={() =>
                      handlePreview("network-list", "Network List")
                    }
                    className="link-color"
                  >
                    {t("network of coverage")}
                  </a>{" "}
                  {t("from the network list (linked)")}
                </label>
              </div>
              <div className={`form-check ${isAr ? "form-check-reverse text-end" : ""}`}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="terms3"
                  checked={understandExclusions}
                  onChange={(e) => setUnderstandExclusions(e.target.checked)}
                />
                <label className="form-check-label">
                  {t("I have read and understood")}{" "}
                  <a
                    onClick={() =>
                      handlePreview("policy-exclusion", "Policy Exclusions")
                    }
                    className="link-color"
                  >
                    {t("policy exclusions")}
                  </a>
                  .
                </label>
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <div className={`form-check mb-3 ${isAr ? "form-check-reverse text-end" : ""}`}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="terms1"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                />
                <label className="form-check-label">
                  {t("Have Seen Affordable")} {" "}
                  <a
                    onClick={() =>
                      handlePreview("table-of-benefits", "Table Of Benefits")
                    }
                    className="link-color"
                  >
                    {t("Table of Benefits")}
                  </a>{" "}
                  {t("I have also understood the")}{" "}
                  <a
                    onClick={() =>
                      handlePreview("network-list", "Network List")
                    }
                    className="link-color"
                  >
                    {t("network of coverage")}
                  </a>{" "}
                  {t("from the network list (linked)")}
                </label>
              </div>

              <div className={`form-check mb-3 ${isAr ? "form-check-reverse text-end" : ""}`}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="terms2"
                  checked={understandCoverage}
                  onChange={(e) => setUnderstandCoverage(e.target.checked)}
                />
                <label className="form-check-label">
                  {t("I have read and understood")}{" "}
                  <a
                    onClick={() =>
                      handlePreview(
                        "covid-19-terms-and-conditions",
                        "Covid-19 Terms And Condition",
                      )
                    }
                    className="link-color"
                  >
                    {t("Covid 19 coverage terms and condition")}
                  </a>
                  .
                </label>
              </div>
              <div className={`form-check ${isAr ? "form-check-reverse text-end" : ""}`}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="terms3"
                  checked={understandExclusions}
                  onChange={(e) => setUnderstandExclusions(e.target.checked)}
                />
                <label className="form-check-label">
                  {t("I have read and understood")}{" "}
                  <a
                    onClick={() =>
                      handlePreview("policy-exclusion", "Policy Exclusions")
                    }
                    className="link-color"
                  >
                    {t("policy exclusions")}
                  </a>
                  .
                </label>
              </div>
            </div>
          )}
          {showUploadStep && submittedPlanDetails && (
            <div className="row mt-4">
              <div className="col-12">
                <UploadDocument planDetails={submittedPlanDetails} />
              </div>
            </div>
          )}

          {/* Proceed Button */}
          <div className="row mt-4">
            <div className={i18n.language === "ar" ? "col-6 text-end" : "col-6 text-start"}>
              <button
                type="button"
                className="btn btn-primary py-2 px-4"
                onClick={handleBack}
              >
                {t("Back")}
              </button>
            </div>
            {!showUploadStep && (
               <div className={i18n.language === "ar" ? "col-6 text-start" : "col-6 text-end"}>
                <button
                  type="button"
                  className="btn btn-primary py-2 px-4"
                  onClick={() => handleSubmit()}
                  disabled={!canProceed}
                >
                  {t("Proceed")}
                </button>
              </div>
            )}
            {!showUploadStep && !canProceed && (
              <div className="col-12 text-danger small mt-2">
                {!allAnswered && (
                  <div>
                    {t("Please answer all questions before proceeding")}.
                  </div>
                )}
                {!isSigned && (
                  <div>
                    {t("Please provide your signature in the box above")}.
                  </div>
                )}
              </div>
            )}
          </div>
          {showConfirm && (
            <>
              <div
                className="modal fade show"
                style={{ display: "block" }}
                role="dialog"
                aria-modal="true"
              >
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    {/* <div className="modal-header border-0">
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={() => setShowConfirm(false)}
                      ></button>
                    </div> */}
                    <div className="modal-body pt-0 pb-0">
                      <UploadDocument planDetails={submittedPlanDetails} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-backdrop fade show"></div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default HealthQuestions;
